<template>
  <v-dialog v-model="shown" width="700">
    <v-card>
      <v-card-title class="mb-5 primary white--text">
        <h2 class="my-5">{{ isRestoreMode ? 'Restore Settings' : 'Archive Settings' }}</h2>
      </v-card-title>
      <v-card-text>
        <div v-if="archiving" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <h3 class="mt-4">{{ isRestoreMode ? 'Restoring' : 'Archiving' }} in progress...</h3>
          <p class="text--secondary">This may take a minute. Please do not close this window.</p>
        </div>
        <div v-else>
          <p v-if="!isRestoreMode">NOTE: This process will archive the existing data and delete this data from the currently active system.</p>
          <p v-else>NOTE: This process will restore archived data to the active system. Any existing data in the selected collections will prevent restoration.</p>
          <p>Please choose which kinds of data you would like to {{ isRestoreMode ? 'restore' : 'archive' }}:</p>

          <div class="archive-selectors">
            <v-checkbox
              aria="Select All"
              label="Select All"
              hide-details
              v-model="allChecked"
              @change="selectAll"
            >
            </v-checkbox>
            <v-checkbox
              v-model="archiveSettings.events"
              :label="isRestoreMode && collectionStatus.events === false ? `Events ${restoreDisabledMessage}` : 'Events'"
              :disabled="isRestoreMode && collectionStatus.events === false"
              hide-details
              @change="checkAllChecked"
            ></v-checkbox>
            <v-checkbox
              v-model="archiveSettings.authors"
              :label="isRestoreMode && collectionStatus.authors === false ? `Authors ${restoreDisabledMessage}` : 'Authors'"
              :disabled="isRestoreMode && collectionStatus.authors === false"
              hide-details
              @change="checkAllChecked"
            ></v-checkbox>
            <v-checkbox
              v-model="archiveSettings.ambassadors"
              :label="isRestoreMode && collectionStatus.ambassadors === false ? `Ambassadors ${restoreDisabledMessage}` : 'Ambassadors'"
              :disabled="isRestoreMode && collectionStatus.ambassadors === false"
              hide-details
              @change="checkAllChecked"
            ></v-checkbox>
            <v-checkbox
              v-model="archiveSettings.locations"
              :label="isRestoreMode && collectionStatus.locations === false ? `Locations ${restoreDisabledMessage}` : 'Locations'"
              :disabled="isRestoreMode && collectionStatus.locations === false"
              hide-details
              @change="checkAllChecked"
            ></v-checkbox>
            <v-checkbox
              v-model="archiveSettings.hotels"
              :label="isRestoreMode && collectionStatus.hotels === false ? `Hotels ${restoreDisabledMessage}` : 'Hotels'"
              :disabled="isRestoreMode && collectionStatus.hotels === false"
              hide-details
              @change="checkAllChecked"
            ></v-checkbox>
          </div>

          <v-text-field
            v-if="!isRestoreMode"
            v-model="archiveSettings.name"
            label="Archive Name"
            hide-details
            placeholder="i.e. '2025 Festival Archive'"
          ></v-text-field>
          <div v-else class="mt-4">
            <strong>Restoring from archive:</strong> {{ archiveName }}
          </div>
        </div>
      </v-card-text>
      <v-card-actions v-if="!archiving">
        <v-btn
          color="error"
          large
          block
          @click="shown = false"
        >
          Cancel<v-icon right>mdi-close</v-icon>
        </v-btn>
        <v-btn
          @click="isRestoreMode ? initiateRestore() : initiateArchiving()"
          color="accent"
          large
          block
        >
          {{ isRestoreMode ? 'Restore' : 'Archive' }}
          <v-icon right>{{ isRestoreMode ? 'mdi-restore' : 'mdi-archive' }}</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { httpsCallable } from 'firebase/functions'
import { collection, getDocs } from 'firebase/firestore'
export default {
  props: {
    isRestoreMode: {
      type: Boolean,
      default: false
    },
    archiveName: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    allChecked: true,
    archiving: false,
    archiveSettings: {
      name: '',
      events: true,
      authors: true,
      ambassadors: true,
      locations: true,
      hotels: true
    },
    shown: false,
    collectionStatus: {
      events: null,
      authors: null,
      ambassadors: null,
      locations: null,
      hotels: null
    }
  }),

  computed: {
    ...mapState([
      'fbFunctions',
      'firestore'
    ]),

    restoreDisabledMessage () {
      return '(please archive or delete existing documents in this collection to restore)'
    }
  },

  watch: {
    shown: {
      async handler (newVal) {
        if (newVal && this.isRestoreMode) {
          await this.checkAllCollectionStatus()
          // Uncheck collections that can't be restored
          const collections = ['events', 'authors', 'ambassadors', 'locations', 'hotels']
          collections.forEach(collection => {
            if (this.collectionStatus[collection] === false) {
              this.archiveSettings[collection] = false
            }
          })
          this.checkAllChecked()
        }
      }
    }
  },

  mounted () {
    this.$root.$on('showArchiveRestoreModal', () => {
      this.shown = true
    })
  },

  methods: {
    ...mapMutations([
      'setGlobalNotification'
    ]),

    async checkCollectionEmpty (collectionName) {
      try {
        const q = collection(this.firestore, collectionName)
        const snapshot = await getDocs(q)
        return snapshot.empty
      } catch (err) {
        console.error(`Error checking ${collectionName}:`, err)
        return false
      }
    },

    async checkAllCollectionStatus () {
      const collections = ['events', 'authors', 'ambassadors', 'locations', 'hotels']
      for (const coll of collections) {
        this.collectionStatus[coll] = await this.checkCollectionEmpty(coll)
      }
    },

    selectAll (value) {
      const collections = ['events', 'authors', 'ambassadors', 'locations', 'hotels']
      collections.forEach(collection => {
        // Only allow selection if not in restore mode or if collection is empty
        if (!this.isRestoreMode || this.collectionStatus[collection] !== false) {
          this.archiveSettings[collection] = value
        }
      })
    },

    checkAllChecked () {
      // Update "Select All" based on whether all individual items are checked
      this.allChecked = this.archiveSettings.events &&
                       this.archiveSettings.authors &&
                       this.archiveSettings.ambassadors &&
                       this.archiveSettings.locations &&
                       this.archiveSettings.hotels
    },

    async initiateArchiving () {
      const userInput = prompt('To confirm archiving, please type "archive" below:')
      if (userInput === 'archive') {
        this.archiving = true
        try {
          const res = await httpsCallable(this.fbFunctions, 'initiateArchiving')(this.archiveSettings)
          console.log(res)
          this.setGlobalNotification({
            val: 'Archiving completed successfully.',
            timeout: -1,
            type: 'success'
          })
          this.$root.$emit('archiveCompleted')
          this.shown = false
        } catch (err) {
          console.log(err)
          this.setGlobalNotification({
            val: 'Error during archiving process.',
            type: 'error'
          })
        } finally {
          this.archiving = false
        }
      } else if (userInput !== null) {
        alert('Archive cancelled. You must type "archive" exactly to proceed.')
      }
    },

    async initiateRestore () {
      const userInput = prompt('To confirm restoration, please type "restore" below:')
      if (userInput === 'restore') {
        this.archiving = true
        try {
          const restoreSettings = {
            archiveName: this.archiveName,
            ...this.archiveSettings
          }
          const res = await httpsCallable(this.fbFunctions, 'initiateRestore')(restoreSettings)
          console.log(res)
          this.setGlobalNotification({
            val: 'Restore completed successfully.',
            timeout: -1,
            type: 'success'
          })
          this.$root.$emit('restoreCompleted')
          this.shown = false
        } catch (err) {
          console.log(err)
          this.setGlobalNotification({
            val: 'Error during restore process: ' + (err.message || 'Unknown error'),
            type: 'error'
          })
        } finally {
          this.archiving = false
        }
      } else if (userInput !== null) {
        alert('Restore cancelled. You must type "restore" exactly to proceed.')
      }
    }
  }
}
</script>

<style lang="scss">
.archive-selectors {
  .v-input:not(:first-child) {
    padding-left: 1.5em;
  }
}
</style>
